import type { ComponentStatus } from "../lib/api";

const LABEL: Record<ComponentStatus, string> = {
  operational: "Operational",
  degraded: "Degraded",
  partial_outage: "Partial outage",
  major_outage: "Major outage",
  maintenance: "Maintenance",
};

export function StatusPill({ status }: { status: ComponentStatus }) {
  return (
    <span
      role="status"
      aria-label={`${LABEL[status]} status`}
      className={`status-pill ${status}`}
    >
      {LABEL[status]}
    </span>
  );
}

/** The headline at the top of the public page.
 *
 * `status` is the rollup over CORE components only - the public API
 * deliberately excludes upstream, because a Slack outage is not a
 * RetrieveIT outage.
 *
 * Taken alone that made the page contradict itself: "All systems
 * operational" sat above a Slack tile reading "Partial outage". Both
 * statements were true of their own scope and the page as a whole was
 * misleading, which on a status page is the only thing that actually
 * matters.
 *
 * So a vendor problem gets its own state rather than being folded into
 * ours. Rolling upstream into the rollup would have fixed the
 * contradiction by overstating the opposite way - a vendor blip would
 * render as a RetrieveIT outage.
 *
 * Note this is reachable without any incident existing, by design: the
 * upstream auto-incident tier only opens at partial_outage, so an
 * upstream component sitting at `degraded` shows a coloured tile and no
 * incident at all. The banner has to speak for that case.
 */
export function OverallBanner({
  status,
  upstreamAffected = 0,
}: {
  status: ComponentStatus;
  upstreamAffected?: number;
}) {
  const headlineByState: Record<ComponentStatus, string> = {
    operational: "All systems operational",
    degraded: "Some systems degraded",
    partial_outage: "Partial outage in progress",
    major_outage: "Major outage",
    maintenance: "Scheduled maintenance",
  };
  const colorByState: Record<ComponentStatus, string> = {
    operational: "bg-primary-50 border-primary-200 text-primary-900",
    degraded: "bg-amber-50 border-amber-200 text-amber-900",
    partial_outage: "bg-red-50 border-red-200 text-red-900",
    major_outage: "bg-red-100 border-red-300 text-red-900",
    maintenance: "bg-indigo-50 border-indigo-200 text-indigo-900",
  };

  const platformOk = status === "operational";
  const note =
    upstreamAffected > 0
      ? `${upstreamAffected} third-party ${
          upstreamAffected === 1 ? "dependency is" : "dependencies are"
        } affected — see Upstream dependencies below`
      : null;

  // Scope the claim when it needs scoping. "All systems" is a promise
  // about everything on the page; "All RetrieveIT systems" is one we can
  // keep while a vendor is down.
  const headline =
    platformOk && note
      ? "All RetrieveIT systems operational"
      : headlineByState[status];

  // Amber, not green: something on this page is coloured, so the banner
  // must not read as all-clear at a glance. Not red either - our
  // platform is genuinely fine.
  const tone = !platformOk
    ? colorByState[status]
    : note
      ? "bg-amber-50 border-amber-200 text-amber-900"
      : colorByState.operational;

  return (
    <div
      role="banner"
      aria-live="polite"
      className={`rounded-lg border px-5 py-4 ${tone}`}
    >
      <p className="text-lg font-semibold">{headline}</p>
      {note ? <p className="mt-1 text-sm font-normal">{note}</p> : null}
    </div>
  );
}
